export const PageType = {
  PDP: "pdp",
  RDP: "rdp",
  Newsletter: "newsletter",
  Cart: "cart",
  Rating: "rating",
  Account: "account",
  Search: "search",
  Sales: "sales",
  Category: "category",
  Campaign: "campaign",
  Static: "static",
  Other: "other",
  Homepage: "homepage",
  Content: "content",
} as const;

export type PageType = (typeof PageType)[keyof typeof PageType];

// NEOSRF-727: With this ticket, we need to send these new page types to the new /page-recommendations endpoint
// With NEOSRF-532 we will deprecate the old page types and only use the new ones.
export const NewPageType = {
  START_PAGE: "START_PAGE",
  PRODUCT_DETAIL_PAGE: "PRODUCT_DETAIL_PAGE",
  ONLINE_CATEGORY: "ONLINE_CATEGORY",
  CONTENT_PAGE: "CONTENT_PAGE",
} as const;

export type NewPageType = (typeof NewPageType)[keyof typeof NewPageType];

const LEGACY_TO_NEW_PAGE_TYPE: Partial<Record<PageType, NewPageType>> = {
  [PageType.Homepage]: NewPageType.START_PAGE,
  [PageType.PDP]: NewPageType.PRODUCT_DETAIL_PAGE,
  [PageType.Category]: NewPageType.ONLINE_CATEGORY,
  [PageType.Content]: NewPageType.CONTENT_PAGE,
};

/**
 * Transforms any PageType or raw fallback string to the new naming schema.
 * @param pageType The legacy PageType to transform
 * @returns The corresponding NewPageType or the original string if no mapping exists
 */
export function toNewPageType(pageType: PageType | string): NewPageType | string {
  if (Object.hasOwn(LEGACY_TO_NEW_PAGE_TYPE, pageType)) {
    return LEGACY_TO_NEW_PAGE_TYPE[pageType as PageType]!;
  }
  return pageType;
}

type PageRule = {
  value: PageType;
  startsWith?: string;
  regex?: RegExp;
};

const PAGE_RULES: readonly PageRule[] = [
  { value: PageType.PDP, startsWith: "/p/" },
  { value: PageType.RDP, startsWith: "/r/" },
  { value: PageType.Newsletter, startsWith: "/c/newsletter" },
  { value: PageType.Cart, startsWith: "/otc/" },
  { value: PageType.Rating, startsWith: "/u/" },
  { value: PageType.Account, startsWith: "/css/" },
  { value: PageType.Search, regex: /^\/q.*search.*/i },
  { value: PageType.Sales, startsWith: "/q/query" },
  { value: PageType.Category, startsWith: "/h/" },
  { value: PageType.Campaign, regex: /^\/c\/.*\/a.*$/i },
  { value: PageType.Static, regex: /^\/c\/.*\/s.*$/i },
  { value: PageType.Other, startsWith: "/c/" },
  { value: PageType.Homepage, regex: /^\/\w{2}-\w{2}\/?$/i },
  { value: PageType.Homepage, startsWith: "/" },
];

const VARIANTS = new Set(["VARIANT", "HEAD_VARIANT", "GENERATED_HEAD_VARIANT"]);
const PRODUCTS_VARIANTS = new Set([
  "FLAT",
  "FIXED_SET",
  "FIXED_SET_COMPONENT",
  "GROUP_ARTICLE_SALES",
  "DIGITAL",
  "RETAIL",
  "NONE",
  "GROUP_ARTICLE_LOGISTICS",
  "LOGISTICS_COMPONENT",
]);

function createFallbackPageType(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  return segments.slice(0, 2).join("/");
}

export function getTrackingPageType(customPathname?: string): PageType | string {
  const pathname = customPathname ?? globalThis.location?.pathname ?? "/";
  const matchedRule = PAGE_RULES.find((rule) => {
    if (rule.startsWith) {
      return pathname.startsWith(rule.startsWith);
    }
    if (rule.regex) {
      return rule.regex.test(pathname);
    }
    return false;
  });

  if (matchedRule) {
    return matchedRule.value;
  }

  return createFallbackPageType(pathname);
}

export function getPDPType(pdp: string): string | undefined {
  if (VARIANTS.has(pdp)) {
    return "v_pdp";
  }
  if (PRODUCTS_VARIANTS.has(pdp)) {
    return "p_pdp";
  }

  return "";
}
